import React, { useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import api from "../api/axios";

export default function ResetPassword() {
	const [searchParams] = useSearchParams();
	const token = searchParams.get("token");
	const navigate = useNavigate();
	
	const [form, setForm] = useState({
		password: "",
		confirmPassword: "",
	});
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [success, setSuccess] = useState(false);
	
	if (!token) {
		return (
			<div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8
			text-center border border-farm-tan/30
			dark:border-gray-700 max-w-sm mx-auto">
			<div className="text-5xl mb-4">⚠️</div>
			<h2 className="text-xl font-bold text-red-600 dark:text-red-400
			mb-2">
			Invalid Reset Link
			</h2>
			<p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
			This link is missing required information. Please request a new one.
			</p>
			<Link
			to="/forgot-password"
			className="text-farm-green dark:text-green-400 font-semibold
			hover:underline"
			>
			Request a new reset link
			</Link>
			</div>
		);
	}
	
	const handleSubmit = async (e) => {
		e.preventDefault();
		setError("");
		if (form.password !== form.confirmPassword) {
			return setError("Passwords do not match.");
		}
		if (form.password.length < 8) {
			return setError("Password must be at least 8 characters.");
		}
		setLoading(true);
		try {
			await api.post("/auth/reset-password", {
				token,
				password: form.password,
			});
			setSuccess(true);
			setTimeout(() => navigate("/login"), 3000);
		} catch (err) {
			setError(
				err.response?.data?.error ||
				"Reset failed. The link may have expired."
			);
		} finally {
			setLoading(false);
		}
	};
	
	if (success) {
		return (
			<div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8
			text-center border border-farm-tan/30
			dark:border-gray-700 max-w-sm mx-auto">
			<div className="text-5xl mb-4">✅</div>
			<h2 className="text-2xl font-bold text-farm-green dark:text-green-400
			mb-2">
			Password Updated!
			</h2>
			<p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
			Your password has been reset. Redirecting to sign in…
			</p>
			<Link
			to="/login"
			className="text-farm-green dark:text-green-400 font-semibold
			hover:underline"
			>
			Sign in now →
			</Link>
			</div>
		);
	}
	
	return (
		<div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8
		border border-farm-tan/30 dark:border-gray-700
		max-w-sm mx-auto transition-colors duration-200">
		<div className="text-center mb-6">
		<div className="text-5xl mb-3">🔒</div>
		<h2 className="text-2xl font-bold text-farm-green dark:text-green-400">
		Set New Password
		</h2>
		<p className="text-gray-500 dark:text-gray-400 text-sm mt-2">
		Choose a strong password for your account.
		</p>
		</div>
		
		{error && (
			<div className="bg-red-50 dark:bg-red-900/30 border border-red-300
			dark:border-red-600 text-red-700 dark:text-red-300
			rounded-lg p-3 mb-4 text-sm">
			{error}
			{error.toLowerCase().includes("expired") && (
				<>
				{" "}
				<Link
				to="/forgot-password"
				className="font-semibold underline"
				>
				Request a new link.
				</Link>
				</>
			)}
			</div>
		)}
		
		<form onSubmit={handleSubmit} className="space-y-4">
		{[
			{ key: "password", label: "New Password",
				placeholder: "At least 8 characters" },
		 { key: "confirmPassword", label: "Confirm New Password",
			 placeholder: "Re-enter new password" },
		].map(({ key, label, placeholder }) => (
			<div key={key}>
			<label className="block text-sm font-medium text-gray-700
			dark:text-gray-300 mb-1">
			{label}
			</label>
			<input
			type="password"
			required
			value={form[key]}
			onChange={(e) => setForm({ ...form, [key]: e.target.value })}
			placeholder={placeholder}
			className="w-full border border-gray-300 dark:border-gray-600
			rounded-lg px-3 py-2 bg-white dark:bg-gray-700
			text-gray-800 dark:text-gray-100
			placeholder-gray-400 dark:placeholder-gray-500
			focus:outline-none focus:ring-2
			focus:ring-farm-green"
			/>
			</div>
		))}
		
		<PasswordStrength password={form.password} />
		
		<button
		type="submit"
		disabled={loading}
		className="w-full bg-farm-green text-white py-3 rounded-xl
		font-semibold hover:bg-green-700 transition-colors
		disabled:opacity-60"
		>
		{loading ? "Updating…" : "Update Password"}
		</button>
		</form>
		</div>
	);
}

function PasswordStrength({ password }) {
	if (!password) return null;
	
	const checks = [
		{ label: "At least 8 characters", pass: password.length >= 8 },
		{ label: "Contains a number",     pass: /\d/.test(password) },
		{ label: "Contains uppercase",    pass: /[A-Z]/.test(password) },
		{ label: "Contains a symbol",     pass: /[^a-zA-Z0-9]/.test(password) },
	];
	
	const score = checks.filter((c) => c.pass).length;
	const colors = [
		"bg-red-400",
		"bg-red-400",
		"bg-orange-400",
		"bg-yellow-400",
		"bg-green-500",
	];
	const labels = ["", "Weak", "Fair", "Good", "Strong"];
	
	return (
		<div className="space-y-1.5">
		<div className="flex gap-1">
		{checks.map((_, i) => (
			<div
			key={i}
			className={`h-1.5 flex-1 rounded-full transition-colors ${
				i < score ? colors[score] : "bg-gray-200 dark:bg-gray-600"
			}`}
			/>
		))}
		</div>
		{score > 0 && (
			<p className="text-xs text-gray-500 dark:text-gray-400">
			Strength:{" "}
			<span className="font-medium text-gray-700 dark:text-gray-300">
			{labels[score]}
			</span>
			</p>
		)}
		</div>
	);
}
